import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useState } from 'react'
import WealthicaAPI, { UNIFIED_ADDON_PREFIX } from 'api/wealthica/services'
import { IWealthicaInitResponse } from 'api/wealthica/models'
import dayjs from 'dayjs'
import http, { IS_WEALTHICA } from 'api/http'

interface IAppContext {
	wealthicaContext?: IWealthicaInitResponse
	isWealthicaContext: boolean
	isFetched: boolean
	isAuthenticated: boolean
	setWealthicaContext?: Dispatch<SetStateAction<IWealthicaInitResponse | undefined>>
	setIsAuthenticated: Dispatch<SetStateAction<boolean>>
	autoImportChecked?: boolean
	setAutoImportChecked: Dispatch<SetStateAction<boolean>>
}

export const AppContext = createContext<IAppContext | undefined>(undefined)

interface IAppContextProps {
	children: ((props: IAppContext) => ReactNode) | ReactNode
}

export function AppProvider({ children }: IAppContextProps) {
	const [wealthicaContext, setWealthicaContext] = useState<IWealthicaInitResponse>()
	const [isFetched, setIsFetched] = useState(false)
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [autoImportChecked, setAutoImportChecked] = useState(false)

	useEffect(() => {
		if (wealthicaContext) {
			http.setStorageItem(IS_WEALTHICA, 'true')
		}
	}, [wealthicaContext])

	const value = useMemo(
		() => ({
			wealthicaContext,
			isWealthicaContext: !!wealthicaContext,
			setWealthicaContext,
			setIsAuthenticated,
			setAutoImportChecked,
			isFetched,
			isAuthenticated,
			autoImportChecked,
		}),
		[wealthicaContext, isFetched, isAuthenticated, autoImportChecked]
	)

	useEffect(() => {
		WealthicaAPI.init()
			.then(async response => {
				if (response) {
					const subscriptionResponse = await WealthicaAPI.getSubscription()
					const subscription = subscriptionResponse.find(
						({ is_active, plan }) => is_active && plan.includes(UNIFIED_ADDON_PREFIX)
					)
					const paidUntilDate = subscription?.expires_at || subscription?.renew_at

					setWealthicaContext({
						...response,
						plan: subscription?.plan,
						paidUntil: paidUntilDate ? dayjs(paidUntilDate).toDate() : undefined,
					} as IWealthicaInitResponse | undefined)
				}
			})
			.finally(() => setIsFetched(true))
	}, [])

	return (
		<AppContext.Provider value={value}>
			{typeof children === 'function' ? (children as (value: IAppContext) => ReactNode)(value) : children}
		</AppContext.Provider>
	)
}

export function useAppContext() {
	const context = useContext(AppContext)

	if (context === undefined) {
		throw new Error('Cannot use "useAppContext" outside the "<AppProvider />" context provider')
	}

	return context
}
